// src/components/CreateMotionModal.js
// Updated: 10/23/2025 09:35 PM EDT
// --- COPY-PASTE THIS ENTIRE FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore'; // Added query, where, getDocs
import { useAuth } from '../context/AuthContext';
import { X, UserCheck, Users } from 'lucide-react'; // Added icons

// Simple loading spinner
const MiniSpinner = () => <div className="h-4 w-4 animate-spin rounded-full border-2 border-dashed border-gray-400"></div>;

export default function CreateMotionModal({ isOpen, onClose }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [voteType, setVoteType] = useState('named'); // 'named' or 'secret'
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser, userData } = useAuth();

  // --- NEW: State for user selection ---
  const [orgUsers, setOrgUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedVoterIds, setSelectedVoterIds] = useState([]); // Store UIDs of selected voters
  const [restrictVoting, setRestrictVoting] = useState(false); // Toggle for restricting voters
  // --- END NEW STATE ---

  // --- NEW: Fetch organization users when modal opens ---
  useEffect(() => {
    if (!isOpen || !userData?.orgId) {
        setOrgUsers([]);
        setSelectedVoterIds([]); // Reset selections when closed/no orgId
        setRestrictVoting(false); // Reset toggle
        return;
    }

    const fetchUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const usersQuery = query(
                collection(db, 'users'),
                where('orgId', '==', userData.orgId)
            );
            const querySnapshot = await getDocs(usersQuery);
            const users = [];
            querySnapshot.forEach((doc) => {
                // Exclude the current admin creating the motion? Optional. Keeping them for now.
                users.push({ id: doc.id, ...doc.data() });
            });
            setOrgUsers(users);
        } catch (err) {
            console.error("Error fetching users for motion:", err);
            setError("Failed to load user list.");
        } finally {
            setIsLoadingUsers(false);
        }
    };

    fetchUsers();
  }, [isOpen, userData?.orgId]);
  // --- END USER FETCH ---

  // --- NEW: Handle voter selection toggle ---
  const toggleVoterSelection = (userId) => {
    setSelectedVoterIds(prev =>
        prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };
  // --- END VOTER SELECTION HANDLER ---


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (title.trim() === '') {
      setError('Title is required.');
      return;
    }
    // --- NEW: Validation for restricted voting ---
    if (restrictVoting && selectedVoterIds.length === 0) {
        setError('If restricting voters, you must select at least one voter.');
        return;
    }
    // --- END VALIDATION ---

    setIsLoading(true);
    setError('');

    try {
      // --- NEW: Prepare motion data with allowedVoters if restricted ---
      const motionData = {
        orgId: userData.orgId,
        createdBy: currentUser.uid,
        title: title,
        description: description,
        voteType: voteType,
        status: 'open',
        createdAt: serverTimestamp(),
        results: { yes: 0, no: 0, abstain: 0, total: 0 },
        // Conditionally add allowedVoters array
        ...(restrictVoting && { allowedVoters: selectedVoterIds })
      };
      // --- END DATA PREPARATION ---

      await addDoc(collection(db, 'motions'), motionData);

      // Reset form and close
      setTitle('');
      setDescription('');
      setVoteType('named');
      setRestrictVoting(false); // Reset toggle
      setSelectedVoterIds([]); // Reset selections
      onClose();

    } catch (err) {
      console.error('Error creating motion:', err);
      setError('Failed to create motion. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"> {/* Added padding */}
      {/* Increased max-w for more space */}
      <div className="relative w-full max-w-xl rounded-lg bg-white p-6 shadow-xl">
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="text-xl font-semibold text-gray-900">Create New Motion</h2>

        {/* Increased vertical spacing */}
        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* Title and Description (as before) */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">Title</label>
            <input type="text" id="title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" required />
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description (optional)</label>
            <textarea id="description" rows="3" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>

          {/* Vote Type (as before) */}
           <div>
             <label className="block text-sm font-medium text-gray-700">Vote Type</label>
             <fieldset className="mt-2">
               <div className="space-y-2 sm:flex sm:items-center sm:space-x-10 sm:space-y-0"> {/* Improved layout */}
                 <div className="flex items-center">
                   <input id="named" name="voteType" type="radio" value="named" checked={voteType === 'named'} onChange={(e) => setVoteType(e.target.value)} className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"/>
                   <label htmlFor="named" className="ml-3 block text-sm text-gray-900">Named (Votes public)</label>
                 </div>
                 <div className="flex items-center">
                   <input id="secret" name="voteType" type="radio" value="secret" checked={voteType === 'secret'} onChange={(e) => setVoteType(e.target.value)} className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500" />
                   <label htmlFor="secret" className="ml-3 block text-sm text-gray-900">Secret (Votes anonymous)</label>
                 </div>
               </div>
             </fieldset>
           </div>


          {/* --- NEW: Allowed Voters Section --- */}
          <div className="space-y-3 rounded-md border border-gray-200 p-4">
             <div className="flex items-center justify-between">
                <h3 className="text-md font-medium text-gray-800">Restrict Voters?</h3>
                <button
                    type="button"
                    onClick={() => setRestrictVoting(!restrictVoting)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        restrictVoting ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        restrictVoting ? 'translate-x-5' : 'translate-x-0'
                    }`}/>
                </button>
             </div>
             {restrictVoting && (
                <div className="pt-3 border-t">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select allowed voters:</label>
                    {isLoadingUsers ? (
                         <div className="flex justify-center p-4"><MiniSpinner /></div>
                    ) : orgUsers.length > 0 ? (
                        <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border p-3 bg-gray-50">
                            {orgUsers.map(user => (
                                <div key={user.id} className="flex items-center justify-between rounded p-2 hover:bg-gray-100">
                                     <div>
                                        <span className="font-medium text-sm text-gray-800">{user.displayName}</span>
                                        <span className="ml-2 text-xs text-gray-500">({user.email})</span>
                                        {user.role === 'admin' && <span className="ml-2 text-xs text-purple-600">(Admin)</span>}
                                     </div>
                                     <button
                                        type="button"
                                        onClick={() => toggleVoterSelection(user.id)}
                                        className={`ml-3 flex-shrink-0 rounded px-2 py-0.5 text-xs font-medium shadow-sm transition ${
                                            selectedVoterIds.includes(user.id)
                                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {selectedVoterIds.includes(user.id) ? <UserCheck className="h-3 w-3 inline mr-1"/> : <Users className="h-3 w-3 inline mr-1"/>}
                                        {selectedVoterIds.includes(user.id) ? 'Selected' : 'Select'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No users found in organization.</p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">{selectedVoterIds.length} voter(s) selected.</p>
                </div>
             )}
              {!restrictVoting && (
                   <p className="text-sm text-gray-500">All members will be allowed to vote.</p>
              )}
          </div>
          {/* --- END ALLOWED VOTERS SECTION --- */}


          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} disabled={isLoading} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50" > Cancel </button>
            <button type="submit" disabled={isLoading} className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50" >
              {isLoading ? 'Creating...' : 'Create Motion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}